import React from 'react';
import { Radio, Stack, Box, Slider, Center, Text, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IJournalResponseInput } from '../../../../global/entities/Journal/Journal';

interface YesNoNumericScaleQuestionProps {
    questionId: number;
    response: IJournalResponseInput | undefined;
    onResponseChange: (questionId: number, updates: Partial<IJournalResponseInput>) => void;
    readOnly?: boolean;
    min?: number;
    max?: number;
    step?: number;
    labelMin?: { en: string; fr: string };
    labelMax?: { en: string; fr: string };
}

export const YesNoNumericScaleQuestion: React.FC<YesNoNumericScaleQuestionProps> = ({
    questionId,
    response,
    onResponseChange,
    readOnly = false,
    min = 0,
    max = 10,
    step = 1,
    labelMin,
    labelMax,
}) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === 'fr' ? 'fr' : 'en';

    const primaryResponse = response?.responseValue;
    const scaleValue = response?.subResponseValue ? parseFloat(response.subResponseValue) : min;

    const normalizedValue = primaryResponse?.toLowerCase() || '';

    const markLabelFor = (value: number): string => {
        if (value === min && labelMin) return `${value} - ${labelMin[currentLang]}`;
        if (value === max && labelMax) return `${value} - ${labelMax[currentLang]}`;
        return value.toString();
    };

    return (
        <Stack gap="md">
            {/* Primary yes/no question */}
            <Radio.Group
                value={normalizedValue}
                onChange={readOnly ? undefined : (value) => {
                    onResponseChange(questionId, {
                        responseValue: value,
                        subResponseValue: value === 'yes' ? min.toString() : '',
                    });
                }}
            >
                <Group gap="md">
                    <Radio
                        value="yes"
                        label={t('journal.yes')}
                    />
                    <Radio
                        value="no"
                        label={t('journal.no')}
                    />
                </Group>
            </Radio.Group>

            {/* Numeric scale if yes */}
            {normalizedValue === 'yes' && (
                <Box p="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)', borderRadius: 4 }}>
                    <Center mb="xl" mt="md">
                        <Text fw={700} size="2rem" style={{ lineHeight: 1 }}>{scaleValue}</Text>
                    </Center>

                    <Box>
                        <Slider
                            value={scaleValue}
                            onChange={readOnly ? undefined : (value) => onResponseChange(questionId, { subResponseValue: value.toString() })}
                            min={min}
                            max={max}
                            step={step}
                            marks={Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => {
                                const markValue = min + i * step;
                                return {
                                    value: markValue,
                                    label: markLabelFor(markValue),
                                };
                            })}
                            mb="md"
                        />
                    </Box>
                </Box>
            )}
        </Stack>
    );
};
